/* eslint-disable react/no-did-update-set-state */
import JsonSchemaForm from '@rjsf/core';
import { customizeValidator } from '@rjsf/validator-ajv8';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import styled from 'styled-components';

import Loading from 'ravenjs/lib/Loading';
import { extractAPIConfig, fetchAPIData } from 'ravenjs/utils/apis';
import { getThemeProps } from 'ravenjs/utils/theme';
import { warning } from 'ravenjs/utils/warning';

import FormActions from './FormActions';
import FormDirtyCheck from './FormDirtyCheck';
import customFields from './fields';
import customWidgets from './widgets';

const FormStyled = styled.div`
    position: relative;

    .RavenSelect__clear-indicator,
    .RavenSelect__dropdown-indicator {
        cursor: pointer;
    }

    ${getThemeProps('Form.styles')};
`;

class Form extends Component {
    static propTypes = {
        /**
         * A list of action buttons for the form
         */
        actionsList: PropTypes.arrayOf(
            PropTypes.shape({
                children: PropTypes.node,
                color: PropTypes.string,
                description: PropTypes.node,
                descriptionPosition: PropTypes.oneOf(['top', 'bottom']),
                onClick: PropTypes.func,
                title: PropTypes.node,
                type: PropTypes.string,
            })
        ),
        /**
         * A title for the form actions group
         */
        actionsTitle: PropTypes.string,
        /**
         * Callback fired after the form is submitted.
         *
         * @param {object} resp The api response
         */
        afterSubmit: PropTypes.func,
        /**
         * A list of api configs to call
         */
        apis: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
        /**
         * A base URL for making the RESTful API calls.
         */
        baseUrl: PropTypes.string,
        /**
         * The contents of the Form.
         */
        children: PropTypes.node,
        /**
         * Custom format validators for ajv (e.g., { username: /^[a-z]+$/ }).
         */
        customFormats: PropTypes.object,
        /**
         * To enable form dirty check operation.
         * If set to 'true', the 'isDirty' flag will be passed in onChange callback params.
         */
        enableDirtyCheck: PropTypes.bool,
        /**
         * Handle form submission through an external source.
         */
        externalSubmission: PropTypes.bool,
        /**
         * Handle multi step form submission.
         */
        multiStepSubmission: PropTypes.bool,
        /**
         * Step id to reset pristine state of form.
         */
        stepId: PropTypes.string,
        /**
         * A set of custom fields for the form.
         */
        fields: PropTypes.object,
        /**
         * The `formContext` which gets passed
         * down to @rjsf/core.
         */
        formContext: PropTypes.object,
        /**
         * Initial `formData`
         */
        formData: PropTypes.oneOfType([
            PropTypes.array,
            PropTypes.bool,
            PropTypes.object,
            PropTypes.string,
            PropTypes.number,
        ]),
        /**
         * A map of action handlers used by rendered components.
         */
        handlers: PropTypes.object,
        /**
         * If you would like to hide the default form actions.
         */
        hideActions: PropTypes.bool,
        /**
         * If `true`, the form validates on every change. Validation is
         * suppressed only during the initial render to prevent errors from
         * flashing on mount.  For multi-step forms (`multiStepSubmission`),
         * validation is additionally deferred until the first submit attempt
         * on each step.
         */
        liveValidate: PropTypes.bool,
        /**
         * A boolean to show the internal `loading` component.
         */
        loading: PropTypes.bool,
        /**
         * The text to be displayed for the internal `loading` component.
         */
        loadingText: PropTypes.string,
        /**
         * An oAuth token to make API calls.
         */
        oAuthToken: PropTypes.string,
        /**
         * A custom ObjectFieldTemplate to override the default one.
         */
        ObjectFieldTemplate: PropTypes.func,
        /**
         * Callback fired when a value changes in the form.
         *
         * @param {Object} form  The form object
         */
        onChange: PropTypes.func,
        /**
         * Callback fired when a form goes out of focus.
         *
         * @param {Object} form  The form object
         */
        onBlur: PropTypes.func,
        /**
         * Callback fired when the form validates and has errors present.
         *
         * @param {Object} errors The form errors
         */
        onError: PropTypes.func,
        /**
         * Callback fired when the form is ready to be submitted.
         *
         * @param {Object} formData The current formData
         */
        onSubmit: PropTypes.func,
        /**
         * If `true`, the form will be marked as read-only.
         */
        readonly: PropTypes.func,
        /**
         * The `schema` for info providing what should the Form render.
         */
        schema: PropTypes.object.isRequired,
        /**
         * Transform the data coming into the form via the API call.
         *
         * @param   {Object} formData The current `formData`
         * @returns {Object}          The transformed `formData`
         */
        transformDataIn: PropTypes.func,
        /**
         * Transform the data going out of the form via the `onSubmit` handler.
         *
         * @returns {Object}  The transformed `formData`
         */
        transformDataOut: PropTypes.func,
        /**
         * A function to transform validation errors before display.
         */
        transformErrors: PropTypes.func,
        /**
         * Custom RJSF template overrides (e.g., { DescriptionFieldTemplate }).
         */
        templates: PropTypes.object,
        /**
         * The `uiSchema` for determining how the Form should be rendered.
         */
        uiSchema: PropTypes.object,
        /**
         * Custom validation function for the form.
         */
        validate: PropTypes.func,
        /**
         * A set of custom widgets for the form.
         */
        widgets: PropTypes.object,
        useSafeSetState: PropTypes.bool,
    };

    static defaultProps = {
        actionsList: [
            {
                children: 'Submit',
                color: 'error',
                onClick: () => {},
            },
        ],
        actionsTitle: '',
        afterSubmit: null,
        apis: {},
        baseUrl: null,
        children: null,
        customFormats: null,
        enableDirtyCheck: false,
        externalSubmission: false,
        multiStepSubmission: false,
        stepId: null,
        fields: null,
        formContext: {},
        formData: {},
        handlers: null,
        hideActions: false,
        liveValidate: false,
        loading: null,
        loadingText: null,
        oAuthToken: null,
        ObjectFieldTemplate: null,
        onChange: null,
        onBlur: null,
        onError: null,
        onSubmit: null,
        readonly: null,
        transformDataIn: null,
        transformDataOut: null,
        transformErrors: null,
        templates: null,
        uiSchema: {},
        validate: null,
        widgets: null,
        useSafeSetState: false,
    };

    // Shared validator with validateSchema disabled so AJV8 tolerates
    // empty enum arrays that are populated at runtime.
    static defaultValidatorInstance = customizeValidator({
        ajvOptionsOverrides: { validateSchema: false },
    });

    constructor(props) {
        super(props);
        // Extract the `formData`, incase we need to transform it.
        const { formData, loading, loadingText } = props;
        // Transform the incoming data.
        const data = this.transformData(formData, 'in');
        // RJSF v5 eagerly validates in its constructor when liveValidate
        // is true. Suppress liveValidate on the very first render so the
        // constructor runs without validation, then allow it on all
        // subsequent renders (matching v1 behaviour: no mount-time errors,
        // but live validation on every user change).
        this._hasCompletedMount = false;
        // Set the state with the updated data.
        this.state = {
            formData: data,
            submittingForm: loading != null ? loading : false,
            submittingFormText: loadingText,
            pristine: true,
            taintedFields: [],
            // Tracks whether a submit has been attempted. Used by
            // multiStepSubmission forms to suppress liveValidate until
            // the user submits the current step, then reset when the
            // step changes
            // so that validation errors do not flash on initial render.
            hasAttemptedSubmit: false,
        };

        this.formDirtyCheck = new FormDirtyCheck();
        this.ref = React.createRef();

        this._isMounted = false;
    }

    componentDidMount() {
        this._isMounted = true;
        this._hasCompletedMount = true;
    }

    componentDidUpdate(prevProps, prevState) {
        const { formData: prevFormData, stepId: stepIdPrevProp } = prevProps;
        const { formData, multiStepSubmission, stepId: stepIdProp } = this.props;
        if (!_isEqual(formData, prevFormData)) {
            const transformed = this.transformData(formData, 'in');
            // Skip if state already reflects the incoming prop (e.g. from a
            // preceding handleOnChange call). This avoids an extra render cycle
            // that RJSF v5 would otherwise turn into a redundant onChange.
            if (!_isEqual(transformed, this.state.formData)) {
                this.safeSetState(() => ({ formData: transformed }));
            }
        }

        if (multiStepSubmission) {
            if (stepIdProp !== stepIdPrevProp) {
                setTimeout(() => {
                    this.safeSetState({
                        pristine: true,
                        taintedFields: [],
                        hasAttemptedSubmit: false,
                    });
                }, 100);
            }
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    safeSetState = (state, callback) => {
        const { useSafeSetState } = this.props;

        if (useSafeSetState) {
            if (this._isMounted) {
                this.setState(state, callback);
            }
        } else {
            this.setState(state, callback);
        }
    };

    /**
     * Return an updated form context
     *
     * @method getFormContext
     * @return {Object}       The updated formContext
     */
    getFormContext = () => {
        const { handlers, formContext } = this.props;
        const { pristine, taintedFields } = this.state;

        return {
            ...formContext,
            getFormData: this.getFormData,
            handlers,
            updateFormData: this.updateFormData,
            pristine,
            taintedFields,
        };
    };

    /**
     * Get the current formData
     *
     * @method getFormData
     * @return {Object}    The current formData
     */
    getFormData = () => {
        const { formData } = this.state;
        return formData;
    };

    /**
     * Get the Form state
     *
     * @method getForm
     * @return {Object} The current form state
     */
    getFormState = () => {
        return this.ref.current.state;
    };

    /**
     * Get the validator instance, creating a custom one if customFormats are provided.
     *
     * @method getValidator
     * @return {Object} The validator instance
     */
    getValidator = () => {
        const { customFormats } = this.props;

        if (customFormats && Object.keys(customFormats).length > 0) {
            if (!this._cachedValidator || this._cachedFormats !== customFormats) {
                this._cachedFormats = customFormats;
                this._cachedValidator = customizeValidator({
                    customFormats,
                    ajvOptionsOverrides: { validateSchema: false },
                });
            }
            return this._cachedValidator;
        }

        return Form.defaultValidatorInstance;
    };

    /**
     * Wrap a consumer's transformErrors callback to ensure backward compatibility
     * with RJSF v5 error objects, which may lack the params property.
     *
     * @method wrapTransformErrors
     * @param {Function} transformErrors
     * @return {Function}
     */
    wrapTransformErrors = transformErrors => errors =>
        transformErrors(
            errors.map(error => ({
                ...error,
                params: error.params || {},
            }))
        );

    /**
     * Wrap a consumer's validate callback to handle RJSF v5 calling it with
     * incomplete formData during intermediate form states. In v1, all nested
     * object properties were pre-initialized; in v5 they may be undefined.
     *
     * @method wrapCustomValidate
     * @param {Function} validate
     * @return {Function}
     */
    wrapCustomValidate = validate => (formData, errors, uiSchema) => {
        try {
            return validate(formData, errors, uiSchema);
        } catch (e) {
            return errors;
        }
    };

    /**
     * Get the current form schema
     *
     * @method getFormSchema
     * @return {Object}
     */
    getFormSchema = () => {
        const { schema } = this.props;
        return schema;
    };

    /**
     * Get the current form uiSchema
     *
     * @method getFormUiSchema
     * @return {Object}
     */
    getFormUiSchema = () => {
        const { readonly, uiSchema } = this.props;
        const { formData } = this.state;

        if (typeof readonly === 'function' && readonly(formData)) {
            return {
                ...uiSchema,
                'ui:readonly': true,
            };
        }

        return uiSchema;
    };

    handleOnChange = form => {
        const { enableDirtyCheck, onChange } = this.props;
        const outputForm = { ...form };

        // Let FormDirtyCheck see every event so it can capture the initial
        // baseline before we potentially bail out of the state update.
        if (enableDirtyCheck) {
            this.formDirtyCheck.onChange(form, this._hasCompletedMount);
            outputForm.isDirty = this.formDirtyCheck.isFormDirty();
        }

        // RJSF v5 fires onChange more aggressively than v1 (e.g. on prop-driven
        // formData updates). Skip the state update when the data hasn't actually
        // changed to avoid unnecessary re-renders and stale-closure races.
        // However, always propagate isDirty so consumers can capture the baseline.
        if (_isEqual(outputForm.formData, this.state.formData)) {
            if (enableDirtyCheck && typeof onChange === 'function') {
                onChange(outputForm);
            }
            return;
        }

        this.safeSetState(
            {
                formData: outputForm.formData,
            },
            () => {
                if (typeof onChange === 'function') {
                    onChange(outputForm);
                }
            }
        );
    };

    handleOnBlur = data => {
        const { taintedFields } = this.state;
        setTimeout(async () => {
            if (!taintedFields.includes(data)) {
                await this.safeSetState({
                    taintedFields: [...taintedFields, data],
                });
            }
        }, 0);
        if (typeof this.props.onBlur === 'function') {
            this.props.onBlur(data);
        }
    };

    handleOnError = errors => {
        const { liveValidate, onError } = this.props;

        // Flip hasAttemptedSubmit so that effectiveLiveValidate activates
        // after the first submit attempt, enabling live validation
        // feedback as the user corrects errors.
        if (liveValidate && !this.state.hasAttemptedSubmit) {
            setTimeout(() => {
                this.safeSetState({ hasAttemptedSubmit: true });
            }, 0);
        }

        // Invoke the `onError` if available.
        typeof onError === 'function' && onError(errors);
    };

    /**
     * Handle the form submission internally, transforming the data if needed.
     *
     * @method handleSubmit
     * @param  {Object}     formProps The Form's onSubmit props
     * @param  {Object}     event
     */
    handleSubmit = (formProps, event) => {
        const { formData } = formProps;
        const { afterSubmit, apis, baseUrl, liveValidate, oAuthToken, onSubmit } = this.props;

        // Flip hasAttemptedSubmit so that effectiveLiveValidate activates
        // after the first submit, enabling live validation feedback.
        if (liveValidate && !this.state.hasAttemptedSubmit) {
            this.safeSetState({ hasAttemptedSubmit: true });
        }
        // Transform the data based on the 'out' func.
        const transformedData = this.transformData(formData, 'out');
        // If `onSubmit` exists then invoke it.
        if (typeof onSubmit === 'function') {
            onSubmit({ ...formProps, formData: transformedData }, event);
        }
        // Check to see if we have a list of `apis`
        // and the `transformedData` is not empty.
        if (!_isEmpty(transformedData) && !_isEmpty(apis)) {
            // Display a warning to user if the `oAuthToken` is empty.
            warning(!_isEmpty(oAuthToken), [
                `A <Form /> requires an oAuthToken to submit the requested data.`,
            ]);
            // Set the `loading` to true.
            this.safeSetState(() => ({
                submittingForm: true,
                submittingFormText: 'Submitting...',
            }));
            // Extract the API config from the schema.
            const api = extractAPIConfig(apis, 'onSubmit');
            // Extract the requeset object from API config.
            const request = _get(api, 'request', {});
            // Extract the requested `dataKey` for the final fetched data.
            const dataKey = _get(api, 'dataKey', 'data');
            // Build the axios config
            const config = {
                ...request,
                baseURL: baseUrl || request.baseURL,
                data: transformedData,
                headers: {
                    Authorization: `Bearer ${oAuthToken}`,
                },
            };
            // Check to see if the `oAuthToken` is not empty
            // before we start to make the API call.
            if (!_isEmpty(oAuthToken) && !_isEmpty(config.baseURL)) {
                // Make the API call.
                fetchAPIData(config, { data: dataKey })
                    .then(resp => {
                        setTimeout(() => {
                            this.safeSetState({
                                submittingForm: false,
                                submittingFormText: 'Submitted!',
                            });
                        });
                        // Extract the data from the response.
                        const data = _get(resp, 'data');
                        // If `afterSubmit` exists then invoke it.
                        if (typeof afterSubmit === 'function') {
                            afterSubmit({ ...formProps, formData: transformedData, resp: data });
                        }
                    })
                    .catch(error => {
                        setTimeout(() => {
                            this.safeSetState({
                                submittingForm: false,
                                submittingFormText: 'Error submitting form!',
                            });
                        });
                        // If `afterSubmit` exists then invoke it.
                        if (typeof afterSubmit === 'function') {
                            afterSubmit({ ...formProps, formData: transformedData, error });
                        }
                    });
            } else {
                setTimeout(() => {
                    this.safeSetState({
                        submittingForm: false,
                        submittingFormText: 'Submitted!',
                    });
                }, 1000);
            }
        }
    };

    /**
     * Transform the `formData` if needed
     *
     * @method transformData
     * @param  {Object}      formData The current `formData`
     * @param  {string}      inOut    Transform 'in' or 'out'?
     * @return {Object}               The transformed `formData`
     */
    transformData = (formData, inOut) => {
        const { transformDataIn, transformDataOut } = this.props;
        // Switch based on `inOut` key
        switch (inOut) {
            case 'in': {
                const data =
                    typeof transformDataIn === 'function' ? transformDataIn(formData) : formData;
                return data;
            }
            case 'out': {
                const data =
                    typeof transformDataOut === 'function' ? transformDataOut(formData) : formData;
                return data;
            }
            default: {
                return formData;
            }
        }
    };

    /**
     * Update the formData with the passed in values
     *
     * @method updateFormData
     * @param  {Object}       formData The passed in (updated) formData
     */
    updateFormData = ({ formData }) => {
        !_isEmpty(formData) &&
            this.safeSetState(prevState => ({
                formData: {
                    ...prevState.formData,
                    ...formData,
                },
            }));
    };

    /**
     * Render the current form's children
     *
     * @method renderChildren
     * @return {node|null}
     */
    renderChildren = () => {
        const {
            actionsList,
            actionsTitle,
            children,
            externalSubmission,
            hideActions,
            readonly,
        } = this.props;
        const { formData } = this.state;

        const disabled = Boolean(typeof readonly === 'function' && readonly(formData));

        if (externalSubmission) {
            return (
                <button
                    ref={btn => (this.submitButton = btn)}
                    style={{ display: 'none' }}
                    type="submit"
                    onClick={() => {
                        setTimeout(() => {
                            this.safeSetState({ pristine: false, hasAttemptedSubmit: true });
                        }, 100);
                    }}
                />
            );
        }

        if (_isEmpty(children)) {
            return (
                <FormActions
                    actions={actionsList}
                    disabled={disabled}
                    hideActions={hideActions}
                    title={actionsTitle}
                />
            );
        }

        return children;
    };

    render() {
        const { formData, hasAttemptedSubmit, submittingForm, submittingFormText } = this.state;
        const {
            children,
            customFormats,
            fields,
            liveValidate,
            templates: templateOverrides,
            loading,
            loadingText,
            ObjectFieldTemplate,
            multiStepSubmission,
            onSubmit,
            readonly,
            schema,
            transformErrors,
            uiSchema,
            validate,
            widgets,
            ...otherProps
        } = this.props;

        // RJSF v5 eagerly validates in its constructor when liveValidate
        // is true, unlike v1 which deferred validation until after the
        // first user interaction. Suppress liveValidate until the form
        // has completed mounting so that forms load without validation
        // errors — matching v1 semantics. Once mounted, liveValidate
        // activates normally. For multi-step forms hasAttemptedSubmit
        // resets on step change so each step starts clean.
        const effectiveLiveValidate = multiStepSubmission
            ? liveValidate && hasAttemptedSubmit
            : liveValidate && this._hasCompletedMount;

        const loadingProps = {
            loading: loading != null ? loading : submittingForm,
            text: loadingText != null && loadingText ? loadingText : submittingFormText,
        };

        const showFormFieldsDescription = _get(schema, 'showFormFieldsDescription', true);
        const FormFieldsDescriptionStyles = _get(schema, 'FormFieldsDescriptionStyles', {});

        return (
            <FormStyled>
                <Loading {...loadingProps} />
                {showFormFieldsDescription && (
                    <customFields.FormDescriptionTextField styles={FormFieldsDescriptionStyles} />
                )}
                <JsonSchemaForm
                    {...otherProps}
                    customValidate={validate ? this.wrapCustomValidate(validate) : undefined}
                    liveValidate={effectiveLiveValidate}
                    transformErrors={
                        transformErrors ? this.wrapTransformErrors(transformErrors) : undefined
                    }
                    fields={{ ...customFields, ...fields }}
                    formContext={this.getFormContext()}
                    formData={formData}
                    onChange={this.handleOnChange}
                    onBlur={this.handleOnBlur}
                    onError={this.handleOnError}
                    onSubmit={this.handleSubmit}
                    schema={this.getFormSchema()}
                    templates={{
                        ArrayFieldTemplate: customFields.ArrayFieldTemplate,
                        BaseInputTemplate: customWidgets.BaseInput,
                        DescriptionFieldTemplate: customFields.DescriptionField,
                        FieldTemplate: customFields.FieldTemplate,
                        ObjectFieldTemplate:
                            ObjectFieldTemplate || customFields.ObjectFieldTemplate,
                        ...templateOverrides,
                    }}
                    uiSchema={this.getFormUiSchema()}
                    validator={this.getValidator()}
                    widgets={{ ...customWidgets, ...widgets }}
                    ref={this.ref}
                >
                    {this.renderChildren()}
                </JsonSchemaForm>
            </FormStyled>
        );
    }
}

export default Form;
